import BaseComponent from "+/Engine/BaseComponent.vue";
import App from "+/Engine/App";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import { Context } from "%/dynamic-components/render-engine/classes-v2/context";
import { resolveContextualAppName, resolveContextEngineAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import DynamicAppError from "%/errors/dynamic-app-error";

const DynamicElementWithContext = {
  extends: BaseComponent,
  inject: {
    // Per-embed scoped contexts provided by an enclosing TemplatedComponentElement
    // (a function, read at call time for reactivity). Shadows the global bag so two
    // embeds of the same component resolve their OWN bound entity, not a shared one.
    injectedRenderScopedContexts: {
      from: "renderScopedContexts",
      default: null,
    },
  },
  computed: {
    elementContext() {
      if (this.editMode) {
        const elementConfig = this.dynamicComponentInstance?.getElementById(this.identifier);
        return elementConfig.context || {};
      }
      return this.configuration.context;
    },
    appName() {
      // Elements persist the builder appName; on a render surface it resolves to the
      // read-only contextual app so App.get/Context.getSource never touch the builder.
      return resolveContextualAppName(this.elementContext.appName);
    },
    // Engine app for App.get (NOT appName, which names the Context source):
    // "Legacy"/platform/un-stamped bindings resolve to the booted contextual app.
    engineAppName() {
      return resolveContextEngineAppName(this.elementContext.appName);
    },
    contextItems() {
      if (this.editMode) {
        return useDynamicComponentBuilderStore().contextItems;
      }
      return { ...Context.allContexts, ...this.injectedRenderScopedContexts?.() ?? {} };
    },
    contextItem() {
      return this.contextItems[this.elementContext.entityContext];
    },
    entityType() {
      return this.contextItem.entityType.split(".").at(-1);
    },
    relatedEntity() {
      if (this.editMode) {
        return useDynamicComponentBuilderStore().allAvailableContextEntities[this.contextItem.entityType];
      }

      const source = Context.getSource(this.appName);
      return source.entities[this.contextItem.entityType];
    },
    extractValuesFromContext() {
      return {
        default: this.defaultValue,
        exampleData: this.exampleData,
      };
    },
    exampleData() {
      if (this.relatedEntity && this.elementContext.field) {
        const field = this.relatedEntity.fields[this.elementContext.field];
        return field.exampleData;
      }
    },
    defaultValue() {
      if (this.relatedEntity && this.elementContext.field) {
        const field = this.relatedEntity.fields[this.elementContext.field];
        return field.default;
      }
    },
    isOptional() {
      if (this.relatedEntity && this.elementContext.field) {
        const field = this.relatedEntity.fields[this.elementContext.field];
        return field.optional;
      }
    },
    valueFromContext: {
      get() {
        if (this.editMode) {
          if (this.isOptional) {
            return this.extractValuesFromContext.exampleData;
          }
          return this.extractValuesFromContext.default ?? this.extractValuesFromContext.exampleData;
        }
        const app = App.get(this.engineAppName);
        const entityType = this.entityType;
        const entityPk = this.contextItem.entityPkUrlVars;
        const fieldName = this.elementContext.field;

        return app.getEntityField(
          entityType,
          entityPk,
          fieldName,
        );
      },
      set(newValue) {
        const app = App.get(this.engineAppName);
        const entityType = this.entityType;
        const entityPk = this.contextItem.entityPkUrlVars;
        const fieldName = this.elementContext.field;
        const params = {
          entityType,
          entityPk,
          fields: { [fieldName]: newValue },
        };
        if (app.getEntityFieldDef(entityType, fieldName)?.stateOnly) {
          app.triggerEventWithoutSendingToServer("UpdateEntityField", params);
          return;
        }
        app.triggerEvent("UpdateEntityField", params);
      },
    },
  },
  created() {
    if (!this.elementContext) {
      throw new DynamicAppError("Cannot use element without context", this);
    }

    if (!this.contextItem) {
      throw new DynamicAppError("Context item is missing", this);
    }

    if (!this.relatedEntity) {
      throw new DynamicAppError("Related entity is missing", this);
    }
  },
  methods: {
    executeAction(event) {
      if (this.editMode) {
        return;
      }

      if (this.stopPropagation) {
        event.stopPropagation();
      }

      const app = App.get(this.engineAppName);
      const entityType = this.entityType;
      const entityPk = this.contextItem.entityPkUrlVars;
      const entity = app.getEntity(entityType, entityPk);

      const actionName = this.elementContext.actionName;
      const eventName = entity.getActionEventName(actionName);
      const contexts = Context.contexts; // for debugging purposes
      const eventParams = entity.getActionEventParams(actionName, contexts);
      app.triggerEvent(eventName, eventParams, contexts);
    },
  },
};

export default DynamicElementWithContext;
